// src/pages/admin/Incentives.tsx
//
// Incentive plans + commission — REAL Shop-owned backend
// (/api/incentives/*), never FileMaker. Plans move through
// draft → legal_review → management_review → active with an audited
// trail; the action buttons render only for the role that may act
// (admin submits, bench-legal reviews, bench-management activates).
// Commission is computed from the REAL imported invoice ledger using
// the Shop-owned rep↔customer assignments (FileMaker carries no
// salesperson attribution — stated plainly on the page).

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BadgeEuro, Gavel, Landmark, Plus, Send, Trash2, Trophy, Users,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { useAuth } from "@/components/auth/AuthContext";
import { authHeaders } from "@/lib/api";
import { formatPrice } from "@/lib/formatPrice";

// ------------------------------------------------------------------ types

interface PlanEvent {
  from_status: string | null;
  to_status: string;
  actor: string;
  note: string | null;
  created_at: string;
}

interface Plan {
  plan_id: string;
  name: string;
  description: string | null;
  metric: string;
  target_value: string | number;
  commission_pct: string | number;
  valid_from: string;
  valid_to: string;
  status: string;
  created_by: string;
  events: PlanEvent[];
}

interface CommissionPlan {
  plan_id: string;
  plan_name: string;
  metric: string;
  base_value: number;
  target: number;
  attainment_pct: number;
  commission_eur: number;
}

interface CommissionRow {
  rep_username: string;
  assigned_customers: number;
  plans: CommissionPlan[];
  unavailable: boolean;
}

interface Assignment {
  rep_username: string;
  customer_id: number;
  customer_name: string;
  assigned_by: string;
}

interface CustomerOpt {
  customer_id: number;
  name: string;
}

const STATUS_VARIANT: Record<string, "default" | "success" | "warning" | "teal" | "danger"> = {
  draft: "default",
  legal_review: "warning",
  management_review: "warning",
  active: "success",
  rejected: "danger",
  archived: "default",
};

const TABS = ["Plans", "Commission", "Assignments"] as const;
type Tab = (typeof TABS)[number];

// Display label for each tab (the tab VALUE stays the machine value).
const TAB_LABEL_KEY: Record<Tab, string> = {
  Plans: "admin.incentives.tabPlans",
  Commission: "admin.incentives.tabCommission",
  Assignments: "admin.incentives.tabAssignments",
};

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...authHeaders(),
    },
  });
  if (r.status === 204) return undefined as T;
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { detail?: unknown };
    throw new Error(
      typeof body.detail === "string" ? body.detail : `HTTP ${r.status}`,
    );
  }
  return (await r.json()) as T;
}

// ==================================================================== page

export function Incentives() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("Plans");
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [commission, setCommission] = useState<CommissionRow[] | null>(null);
  const [attribution, setAttribution] = useState("");
  const [assignments, setAssignments] = useState<Assignment[] | null>(null);
  const [error, setError] = useState("");

  const isAdmin = auth.accountType === "admin";
  const isLegal = auth.accountType === "legal";
  const isManagement = auth.accountType === "management";

  const refresh = useCallback(() => {
    if (!auth.isReady || !auth.isAuthenticated) return;
    setError("");
    api<{ rows: Plan[] }>("/api/incentives/plans")
      .then((b) => setPlans(b.rows))
      .catch((e) => setError(String(e instanceof Error ? e.message : e)));
    api<{ rows: CommissionRow[]; attribution: string }>("/api/incentives/commission")
      .then((b) => {
        setCommission(b.rows);
        setAttribution(b.attribution);
      })
      .catch(() => setCommission([]));
    api<{ rows: Assignment[] }>("/api/incentives/assignments")
      .then((b) => setAssignments(b.rows))
      .catch(() => setAssignments([]));
  }, [auth.isReady, auth.isAuthenticated]);

  useEffect(refresh, [refresh]);

  const act = (path: string, body?: unknown, method = "POST") =>
    api(path, {
      method,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
      .then(refresh)
      .catch((e) => setError(String(e instanceof Error ? e.message : e)));

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-510 tracking-tight text-fg-primary">
          {t("admin.incentives.title")}
        </h1>
        <p className="mt-1 text-sm text-fg-tertiary">
          {t("admin.incentives.subtitle")}
        </p>
      </header>

      <div className="flex gap-1 border-b border-border">
        {TABS.map((tabKey) => (
          <button
            key={tabKey}
            onClick={() => setTab(tabKey)}
            className={
              "px-4 py-2 text-sm font-510 transition-colors " +
              (tab === tabKey
                ? "border-b-2 border-benchmark-teal text-fg-primary"
                : "text-fg-tertiary hover:text-fg-primary")
            }
          >
            {t(TAB_LABEL_KEY[tabKey])}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
          {error}
        </div>
      )}

      {tab === "Plans" && (
        <PlansTab
          plans={plans}
          isAdmin={isAdmin}
          isLegal={isLegal}
          isManagement={isManagement}
          onCreated={refresh}
          onAction={act}
        />
      )}
      {tab === "Commission" && (
        <CommissionTab commission={commission} attribution={attribution} />
      )}
      {tab === "Assignments" && (
        <AssignmentsTab
          assignments={assignments}
          isAdmin={isAdmin}
          attribution={attribution}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- plans tab

function PlansTab({
  plans,
  isAdmin,
  isLegal,
  isManagement,
  onCreated,
  onAction,
}: {
  plans: Plan[] | null;
  isAdmin: boolean;
  isLegal: boolean;
  isManagement: boolean;
  onCreated: () => void;
  onAction: (path: string, body?: unknown) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  if (plans === null)
    return <p className="text-sm text-fg-tertiary">{t("admin.incentives.loading")}</p>;
  return (
    <div className="space-y-4">
      {isAdmin && (
        <div>
          <Button variant="secondary" size="sm" onClick={() => setShowForm((s) => !s)}>
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("admin.incentives.newPlan")}
          </Button>
          {showForm && <PlanForm onCreated={() => { setShowForm(false); onCreated(); }} />}
        </div>
      )}
      {plans.length === 0 && (
        <Card>
          <CardContent className="p-6 text-sm text-fg-tertiary">
            {t("admin.incentives.emptyPlans")} {" "}
            {isAdmin
              ? t("admin.incentives.emptyPlansAdmin")
              : t("admin.incentives.emptyPlansNonAdmin")}
          </CardContent>
        </Card>
      )}
      {plans.map((p) => (
        <PlanCard
          key={p.plan_id}
          plan={p}
          isAdmin={isAdmin}
          isLegal={isLegal}
          isManagement={isManagement}
          onAction={onAction}
        />
      ))}
    </div>
  );
}

function PlanForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    description: "",
    metric: "revenue_htva",
    target_value: "50000",
    commission_pct: "5",
    valid_from: new Date().toISOString().slice(0, 10),
    valid_to: new Date(new Date().getFullYear(), 11, 31).toISOString().slice(0, 10),
  });
  const [error, setError] = useState("");
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Card className="mt-3">
      <CardContent className="grid gap-3 p-4 sm:grid-cols-2">
        {error && <div className="sm:col-span-2 text-sm text-accent-rose">{error}</div>}
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-xs uppercase tracking-wider text-fg-tertiary">
            {t("admin.incentives.planName")}
          </span>
          <input value={form.name} onChange={(e) => set("name", e.target.value)}
            placeholder={t("admin.incentives.planNamePlaceholder")}
            className="w-full rounded border border-border bg-bg-surface px-3 py-2 text-sm text-fg-primary" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wider text-fg-tertiary">
            {t("admin.incentives.metric")}
          </span>
          <select value={form.metric} onChange={(e) => set("metric", e.target.value)}
            className="h-9 w-full rounded border border-border bg-bg-surface px-3 text-sm text-fg-primary">
            <option value="revenue_htva">{t("admin.incentives.metricRevenue")}</option>
            <option value="invoice_count">{t("admin.incentives.metricInvoiceCount")}</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wider text-fg-tertiary">
            {t("admin.incentives.commissionPct")}
          </span>
          <input value={form.commission_pct} onChange={(e) => set("commission_pct", e.target.value)} type="number" min="0" max="100" step="0.5"
            className="w-full rounded border border-border bg-bg-surface px-3 py-2 text-sm text-fg-primary" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wider text-fg-tertiary">
            {t("admin.incentives.target")}
          </span>
          <input value={form.target_value} onChange={(e) => set("target_value", e.target.value)} type="number" min="1"
            className="w-full rounded border border-border bg-bg-surface px-3 py-2 text-sm text-fg-primary" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-fg-tertiary">
              {t("admin.incentives.from")}
            </span>
            <input value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} type="date"
              className="w-full rounded border border-border bg-bg-surface px-3 py-2 text-sm text-fg-primary" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-fg-tertiary">
              {t("admin.incentives.to")}
            </span>
            <input value={form.valid_to} onChange={(e) => set("valid_to", e.target.value)} type="date"
              className="w-full rounded border border-border bg-bg-surface px-3 py-2 text-sm text-fg-primary" />
          </label>
        </div>
        <div className="sm:col-span-2">
          <Button size="sm" onClick={() => {
            setError("");
            api("/api/incentives/plans", {
              method: "POST",
              body: JSON.stringify({
                name: form.name,
                description: form.description || null,
                metric: form.metric,
                target_value: Number(form.target_value),
                commission_pct: Number(form.commission_pct),
                valid_from: form.valid_from,
                valid_to: form.valid_to,
              }),
            }).then(onCreated)
              .catch((e) => setError(String(e instanceof Error ? e.message : e)));
          }}>
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("admin.incentives.createDraft")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PlanCard({
  plan: p,
  isAdmin,
  isLegal,
  isManagement,
  onAction,
}: {
  plan: Plan;
  isAdmin: boolean;
  isLegal: boolean;
  isManagement: boolean;
  onAction: (path: string, body?: unknown) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [showTrail, setShowTrail] = useState(false);
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            {p.name}
            <Badge variant={STATUS_VARIANT[p.status] ?? "default"} size="sm">
              {p.status.replace(/_/g, " ")}
            </Badge>
          </CardTitle>
          <CardDescription>
            {p.metric === "revenue_htva"
              ? t("admin.incentives.targetRevenue")
              : t("admin.incentives.targetInvoiceCount")}{" "}
            {p.metric === "revenue_htva"
              ? formatPrice(Number(p.target_value), "EUR")
              : Number(p.target_value)}
            {" · "}
            {t("admin.incentives.commissionSummary", {
              pct: Number(p.commission_pct),
              from: p.valid_from,
              to: p.valid_to,
            })}
          </CardDescription>
        </div>
        <div className="flex shrink-0 gap-2">
          {isAdmin && p.status === "draft" && (
            <Button size="sm" onClick={() => void onAction(`/api/incentives/plans/${p.plan_id}/submit`)}>
              <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t("admin.incentives.submitToLegal")}
            </Button>
          )}
          {isLegal && p.status === "legal_review" && (
            <>
              <Button size="sm" onClick={() => void onAction(`/api/incentives/plans/${p.plan_id}/legal`, { approve: true })}>
                <Gavel className="h-3.5 w-3.5" strokeWidth={1.5} />
                {t("admin.incentives.legalApprove")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => void onAction(`/api/incentives/plans/${p.plan_id}/legal`, { approve: false, note: "rejected by legal" })}>
                {t("admin.incentives.reject")}
              </Button>
            </>
          )}
          {isManagement && p.status === "management_review" && (
            <>
              <Button size="sm" onClick={() => void onAction(`/api/incentives/plans/${p.plan_id}/management`, { approve: true })}>
                <Landmark className="h-3.5 w-3.5" strokeWidth={1.5} />
                {t("admin.incentives.activate")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => void onAction(`/api/incentives/plans/${p.plan_id}/management`, { approve: false, note: "rejected by management" })}>
                {t("admin.incentives.reject")}
              </Button>
            </>
          )}
          {isAdmin && p.status !== "archived" && p.status !== "draft" && (
            <Button size="sm" variant="ghost" onClick={() => void onAction(`/api/incentives/plans/${p.plan_id}/archive`)}>
              {t("admin.incentives.archive")}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {p.description && <p className="mb-2 text-sm text-fg-secondary">{p.description}</p>}
        <button
          className="text-xs text-fg-tertiary underline underline-offset-2 hover:text-fg-primary"
          onClick={() => setShowTrail((s) => !s)}
        >
          {showTrail
            ? t("admin.incentives.hideAuditTrail")
            : t("admin.incentives.showAuditTrail")}{" "}
          {t("admin.incentives.auditTrail", { count: p.events.length })}
        </button>
        {showTrail && (
          <div className="mt-2 space-y-1 border-t border-border pt-2">
            {p.events.map((e, i) => (
              <div key={i} className="text-xs text-fg-tertiary">
                <span className="text-fg-secondary">{e.from_status ?? "∅"} → {e.to_status}</span>
                {" · "}{e.actor}{" · "}{new Date(e.created_at).toLocaleString()}
                {e.note ? ` · ${e.note}` : ""}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------ commission tab

function CommissionTab({
  commission,
  attribution,
}: {
  commission: CommissionRow[] | null;
  attribution: string;
}) {
  const { t } = useTranslation();
  if (commission === null)
    return <p className="text-sm text-fg-tertiary">{t("admin.incentives.loading")}</p>;
  if (commission.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-fg-tertiary">
          {t("admin.incentives.commissionUnavailable")}
        </CardContent>
      </Card>
    );
  }
  const ranked = [...commission].sort(
    (a, b) =>
      b.plans.reduce((s, p) => s + p.commission_eur, 0) -
      a.plans.reduce((s, p) => s + p.commission_eur, 0),
  );
  return (
    <div className="space-y-4">
      {ranked.map((row, rank) => (
        <Card key={row.rep_username}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                {rank === 0 && <Trophy className="h-4 w-4 text-accent-amber" strokeWidth={1.5} />}
                {row.rep_username}
              </CardTitle>
              <CardDescription className="flex items-center gap-1">
                <Users className="h-3 w-3" strokeWidth={1.5} />
                {row.assigned_customers === 1
                  ? t("admin.incentives.assignedCustomers", {
                      count: row.assigned_customers,
                    })
                  : t("admin.incentives.assignedCustomersOther", {
                      count: row.assigned_customers,
                    })}
              </CardDescription>
            </div>
            <div className="text-right">
              <div className="text-xl font-510 text-fg-primary">
                {formatPrice(row.plans.reduce((s, p) => s + p.commission_eur, 0), "EUR")}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-fg-quaternary">
                {t("admin.incentives.totalCommission")}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {row.plans.length === 0 && (
              <p className="text-sm text-fg-tertiary">
                {t("admin.incentives.noActivePlan")}
              </p>
            )}
            {row.plans.map((p) => (
              <div key={p.plan_id} className="space-y-1">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-fg-secondary">
                    {p.plan_name}
                    <span className="ml-2 text-xs text-fg-quaternary">{p.plan_id}</span>
                  </span>
                  <span className="font-510 text-fg-primary">
                    {formatPrice(p.commission_eur, "EUR")}
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-bg-elevated">
                  <div
                    className={
                      "h-full rounded-full " +
                      (p.attainment_pct >= 100 ? "bg-benchmark-teal" : "bg-accent-indigo")
                    }
                    style={{ width: `${Math.min(p.attainment_pct, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-[11px] text-fg-quaternary">
                  <span>
                    {p.metric === "revenue_htva"
                      ? t("admin.incentives.invoicedHtvaOf", {
                          invoiced: formatPrice(p.base_value, "EUR"),
                          target: formatPrice(p.target, "EUR"),
                        })
                      : t("admin.incentives.invoicesOf", {
                          count: p.base_value,
                          target: p.target,
                        })}
                  </span>
                  <span className={p.attainment_pct >= 100 ? "text-benchmark-teal-bright" : ""}>
                    {p.attainment_pct}%
                  </span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
      <p className="text-[11px] text-fg-quaternary">
        {t("admin.incentives.attributionNote", { attribution })}
      </p>
    </div>
  );
}

// ----------------------------------------------------------- assignments tab

function AssignmentsTab({
  assignments,
  isAdmin,
  attribution,
  onChanged,
}: {
  assignments: Assignment[] | null;
  isAdmin: boolean;
  attribution: string;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [rep, setRep] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState<CustomerOpt[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isAdmin) return;
    api<{ rows: CustomerOpt[] }>("/api/customers?limit=500")
      .then((b) => setCustomers(b.rows))
      .catch(() => setCustomers([]));
  }, [isAdmin]);

  if (assignments === null)
    return <p className="text-sm text-fg-tertiary">{t("admin.incentives.loading")}</p>;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BadgeEuro className="h-4 w-4 text-accent-green" strokeWidth={1.5} />
            {t("admin.incentives.assignmentsTitle")}
          </CardTitle>
          <CardDescription>
            {t("admin.incentives.assignmentsDescription", { attribution })}
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border p-0">
          {assignments.length === 0 && (
            <p className="px-5 py-3 text-sm text-fg-tertiary">
              {t("admin.incentives.noAssignments")}
            </p>
          )}
          {assignments.map((a) => (
            <div key={`${a.rep_username}-${a.customer_id}`}
              className="flex items-center gap-3 px-5 py-2.5 text-sm">
              <span className="font-510 text-fg-primary">{a.rep_username}</span>
              <span className="text-fg-tertiary">→</span>
              <span className="text-fg-secondary">
                {a.customer_name}
                <span className="ml-1 text-xs text-fg-quaternary">#{a.customer_id}</span>
              </span>
              {isAdmin && (
                <button
                  aria-label={t("admin.incentives.removeAssignmentAria", {
                    rep: a.rep_username,
                    customer: a.customer_name,
                  })}
                  className="ml-auto text-fg-quaternary hover:text-accent-rose"
                  onClick={() => {
                    api(`/api/incentives/assignments/${encodeURIComponent(a.rep_username)}/${a.customer_id}`,
                      { method: "DELETE" })
                      .then(onChanged)
                      .catch((e) => setError(String(e instanceof Error ? e.message : e)));
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
      {isAdmin && (
        <Card>
          <CardContent className="flex flex-wrap items-end gap-3 p-4">
            {error && <div className="w-full text-sm text-accent-rose">{error}</div>}
            <label className="block">
              <span className="mb-1 block text-xs uppercase tracking-wider text-fg-tertiary">
                {t("admin.incentives.repUsername")}
              </span>
              <input value={rep} onChange={(e) => setRep(e.target.value)} placeholder="sales-1"
                className="w-48 rounded border border-border bg-bg-surface px-3 py-2 text-sm text-fg-primary" />
            </label>
            <label className="block min-w-64">
              <span className="mb-1 block text-xs uppercase tracking-wider text-fg-tertiary">
                {t("admin.incentives.customer")}
              </span>
              <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
                className="h-9 w-full rounded border border-border bg-bg-surface px-3 text-sm text-fg-primary">
                <option value="">{t("admin.incentives.select")}</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name} (#{c.customer_id})
                  </option>
                ))}
              </select>
            </label>
            <Button size="sm" disabled={!rep.trim() || !customerId}
              onClick={() => {
                setError("");
                api("/api/incentives/assignments", {
                  method: "POST",
                  body: JSON.stringify({ rep_username: rep.trim(), customer_id: Number(customerId) }),
                }).then(() => { setRep(""); setCustomerId(""); onChanged(); })
                  .catch((e) => setError(String(e instanceof Error ? e.message : e)));
              }}>
              <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t("admin.incentives.assign")}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
